import React, { useState, useEffect } from 'react';

interface LinkPreviewProps {
  url: string;
  className?: string;
  maxWidth?: string;
  showDescription?: boolean;
  showImage?: boolean;
  fallbackImage?: string;
  onlyImage?: boolean;
}

interface LinkMetadata {
  title: string;
  description: string;
  image: string;
  siteName: string;
  url: string;
}

const LinkPreview: React.FC<LinkPreviewProps> = ({
  url,
  className,
  maxWidth = '400px',
  showDescription = true,
  showImage = true,
  fallbackImage,
  onlyImage = false
}) => {
  const [previewData, setPreviewData] = useState<LinkMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMetadata = async () => {
      if (!url) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Try to fetch the webpage content
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'User-Agent': 'Mozilla/5.0 (compatible; LinkPreview/1.0)'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Extract Open Graph metadata
        const ogTitle = doc.querySelector('meta[property="og:title"]')?.getAttribute('content');
        const ogDescription = doc.querySelector('meta[property="og:description"]')?.getAttribute('content');
        const ogImage = doc.querySelector('meta[property="og:image"]')?.getAttribute('content');
        const ogSiteName = doc.querySelector('meta[property="og:site_name"]')?.getAttribute('content');

        // Fallback to regular meta tags if Open Graph is not available
        const title = ogTitle || doc.querySelector('title')?.textContent || doc.querySelector('meta[name="title"]')?.getAttribute('content') || '';
        const description = ogDescription || doc.querySelector('meta[name="description"]')?.getAttribute('content') || '';
        const image = ogImage || fallbackImage || '';
        const siteName = ogSiteName || new URL(url).hostname;

        setPreviewData({
          title: title.trim(),
          description: description.trim(),
          image: image,
          siteName: siteName,
          url: url
        });

      } catch (error) {
        console.error('Error fetching link preview:', error);
        setError('Failed to fetch link preview');
        
        // Set fallback data
        try {
          const urlObj = new URL(url);
          setPreviewData({
            title: urlObj.hostname + (urlObj.pathname !== '/' ? urlObj.pathname : ''),
            description: 'Unable to fetch link details',
            image: fallbackImage || '',
            siteName: urlObj.hostname,
            url: url
          });
        } catch (urlError) {
          setPreviewData({
            title: url,
            description: 'Invalid URL',
            image: fallbackImage || '',
            siteName: 'Unknown',
            url: url
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMetadata();
  }, [url, fallbackImage]);

  const handleClick = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div 
        className={`link-preview loading ${className || ''}`}
        style={{ maxWidth }}
        aria-label="Loading link preview"
      >
        <div className="link-preview-skeleton">
          <div className="skeleton-image"></div>
          <div className="skeleton-content">
            <div className="skeleton-title"></div>
            <div className="skeleton-description"></div>
            <div className="skeleton-site"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !previewData) {
    return (
      <div 
        className={`link-preview error ${className || ''}`}
        style={{ maxWidth }}
        aria-label="Link preview error"
      >
        <div className="link-preview-error">
          <span className="error-icon">⚠️</span>
          <span className="error-text">Unable to load link preview</span>
        </div>
      </div>
    );
  }

  if (!previewData) {
    return null;
  }

  // Return only image preview when onlyImage prop is true
  if (onlyImage) {
    return (
      <div 
        className={`link-preview-image-only ${className || ''}`}
        style={{ maxWidth }}
        aria-label={`Image preview for ${previewData.title}`}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {previewData.image ? (
          <img 
            src={previewData.image} 
            alt={previewData.title || 'Link preview image'}
            loading="lazy"
            style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              img.style.display = 'none';
              const fallback = img.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
        ) : (
          <>
          </>
        )}
      </div>
    );
  }

  return (
    <div 
      className={`link-preview ${className || ''}`}
      style={{ maxWidth }}
      aria-label={`Link preview for ${previewData.title}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="link-preview-content">
        {showImage && previewData.image && (
          <div className="link-preview-image">
            <img 
              src={previewData.image} 
              alt={previewData.title || 'Link preview image'}
              loading="lazy"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
                const fallback = img.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            <div className="image-fallback" style={{ display: 'none' }}>
              <span>{previewData.siteName?.charAt(0) || 'L'}</span>
            </div>
          </div>
        )}
        
        <div className="link-preview-info">
          <div className="link-preview-header">
            {previewData.siteName && (
              <span className="link-preview-site" title={previewData.siteName}>
                {previewData.siteName}
              </span>
            )}
            <span className="link-preview-url" title={previewData.url}>
              {new URL(previewData.url).hostname}
            </span>
          </div>
          
          <h3 className="link-preview-title" title={previewData.title}>
            {previewData.title || 'Untitled'}
          </h3>
          
          {showDescription && previewData.description && (
            <p className="link-preview-description" title={previewData.description}>
              {previewData.description}
            </p>
          )}
          
          <div className="link-preview-footer">
            <span className="link-preview-link-text">Visit Link</span>
            <span className="link-preview-arrow">→</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkPreview;
